import React, { useState, useEffect } from "react";
import { GiMoneyStack } from "react-icons/gi";
import { BiTimeFive } from "react-icons/bi";
import { BsWallet, BsCashStack } from "react-icons/bs";
import { AiOutlineShoppingCart, AiOutlineClose } from "react-icons/ai";
import { useGetBalanceQuery } from "../redux/apislice";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Player } from '@lottiefiles/react-lottie-player';

const mockTransactions = [
  {
    id: "TXN001",
    orderId: "ORD001",
    amount: "Rs. 15,000",
    date: "2025-06-30",
    status: "Paid",
    method: "Bank Transfer",
  },
  {
    id: "TXN002",
    orderId: "ORD002",
    amount: "Rs. 10,000",
    date: "2025-06-28",
    status: "On Hold",
    method: "JazzCash",
  },
  {
    id: "TXN003",
    orderId: "ORD003",
    amount: "Rs. 8,000",
    date: "2025-06-25",
    status: "Paid",
    method: "Easypaisa",
  },
];

const SummaryCard = ({ title, amount, icon, color, showEyeIcon = false, onEyeClick }) => (
  <div className={`bg-${color}-100 text-${color}-800 rounded-lg p-4 shadow`}> 
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <h2 className="text-xl font-bold">{amount}</h2>
      </div>
      <div className="flex items-center">
        <div className="text-3xl mr-2">{icon}</div>
        {showEyeIcon && (
          <button onClick={onEyeClick} className="text-gray-500 hover:text-gray-700">
            <FontAwesomeIcon icon={amount.includes('****') ? faEye : faEyeSlash} className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  </div>
);

const TransactionTable = ({ data }) => (
  <div className="overflow-x-auto bg-white rounded-lg shadow mt-6">
    <table className="min-w-full">
      <thead className="bg-black text-white">
        <tr>
          <th className="px-4 py-2 text-left">Transaction ID</th>
          <th className="px-4 py-2 text-left">Order ID</th>
          <th className="px-4 py-2 text-left">Amount</th>
          <th className="px-4 py-2 text-left">Date</th>
          <th className="px-4 py-2 text-left">Status</th>
          <th className="px-4 py-2 text-left">Method</th>
        </tr>
      </thead>
      <tbody>
        {data.map((txn) => (
          <tr key={txn.id} className="border-b hover:bg-gray-50">
            <td className="px-4 py-2">{txn.id}</td>
            <td className="px-4 py-2">{txn.orderId}</td>
            <td className="px-4 py-2">{txn.amount}</td>
            <td className="px-4 py-2">{txn.date}</td>
            <td className="px-4 py-2">{txn.status}</td>
            <td className="px-4 py-2">{txn.method}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const WithdrawalModal = ({ isOpen, onClose, onSubmit }) => {
  const [withdrawalMethod, setWithdrawalMethod] = useState("bank");
  const [bankName, setBankName] = useState("");
  const [accountTitle, setAccountTitle] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [iban, setIban] = useState("");
  const [mobileWalletName, setMobileWalletName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [amount, setAmount] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const withdrawalData = {
      method: withdrawalMethod,
      amount,
      ...(withdrawalMethod === "bank" ? {
        bankName,
        accountTitle,
        accountNumber,
        iban
      } : {
        mobileWalletName,
        mobileNumber
      })
    };
    onSubmit(withdrawalData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Request Payout</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <AiOutlineClose size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Withdrawal Method</label>
            <select
              value={withdrawalMethod}
              onChange={(e) => setWithdrawalMethod(e.target.value)}
              className="w-full p-2 border rounded"
              required
            >
              <option value="bank">Bank Transfer</option>
              <option value="jazzcash">JazzCash</option>
              <option value="easypaisa">EasyPaisa</option>
              <option value="nayapay">NayaPay</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Amount (Rs.)</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Enter amount"
              required
            />
          </div>

          {withdrawalMethod === "bank" ? (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Bank Name</label>
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter bank name"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">IBAN Number</label>
                <input
                  type="text"
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter IBAN number"
                  required
                />
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  {withdrawalMethod === "jazzcash" ? "JazzCash" : 
                   withdrawalMethod === "easypaisa" ? "EasyPaisa" : "NayaPay"} Account Name
                </label>
                <input
                  type="text"
                  value={mobileWalletName}
                  onChange={(e) => setMobileWalletName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder={`Enter ${withdrawalMethod === "jazzcash" ? "JazzCash" : 
                              withdrawalMethod === "easypaisa" ? "EasyPaisa" : "NayaPay"} account name`}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  {withdrawalMethod === "jazzcash" ? "JazzCash" : 
                   withdrawalMethod === "easypaisa" ? "EasyPaisa" : "NayaPay"} Number
                </label>
                <input
                  type="text"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder={`Enter ${withdrawalMethod === "jazzcash" ? "JazzCash" : 
                              withdrawalMethod === "easypaisa" ? "EasyPaisa" : "NayaPay"} number`}
                  required
                />
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
            >
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function PaymentDashboard() {
  const { isLoading, data } = useGetBalanceQuery();
  const [transactions, setTransactions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAvailableBalance, setShowAvailableBalance] = useState(false);
  const [balanceTimer, setBalanceTimer] = useState(null);

  useEffect(() => {
    setTransactions(mockTransactions);
  }, []);

  const toggleBalanceVisibility = () => {
    if (balanceTimer) {
      clearTimeout(balanceTimer);
    }
    setShowAvailableBalance(true);
    const timer = setTimeout(() => {
      setShowAvailableBalance(false);
    }, 10000);
    setBalanceTimer(timer);
  };

  useEffect(() => {
    return () => {
      if (balanceTimer) {
        clearTimeout(balanceTimer);
      }
    };
  }, [balanceTimer]);

  const handleRequestPayout = (withdrawalData) => {
    console.log("Withdrawal request submitted:", withdrawalData);
    alert(`Payout request for Rs. ${withdrawalData.amount} submitted successfully!`);
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
      }}>
        <Player
          autoplay
          loop
          src="https://assets1.lottiefiles.com/packages/lf20_raiw2hpe.json"
          style={{ height: '150px', width: '150px' }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen w-full bg-gray-50">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6">Payments Overview</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard title="Total Sales" amount={`Rs. ${data?.data?.totalEarned}`} icon={<GiMoneyStack />} color="green" />
        <SummaryCard title="On-Hold Payments" amount={`Rs. ${data?.data?.oh_hold}`} icon={<BiTimeFive />} color="yellow" />
        <SummaryCard 
          title="Available Balance" 
          amount={showAvailableBalance ? `Rs. ${data?.data?.available}` : "Rs. ****"} 
          icon={<BsWallet />} 
          color="blue"
          showEyeIcon={true}
          onEyeClick={toggleBalanceVisibility}
        />
        <SummaryCard title="Total Orders" amount={data?.data?.order} icon={<AiOutlineShoppingCart />} color="indigo" />
      </div>

       <div className="bg-white rounded-lg shadow p-6 mb-6">
  <div className="flex flex-col md:flex-row gap-6">
    {/* Payment Method Section - Takes 2/3 width on larger screens */}
    <div className="flex-1 md:flex-[2]">
      <h3 className="text-lg font-semibold mb-4">Payment Method</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-gray-600"><strong>Method:</strong></p>
          <p className="font-medium">Bank Transfer</p>
        </div>
        <div>
          <p className="text-gray-600"><strong>Account Title:</strong></p>
          <p className="font-medium">Arbab Irfan</p>
        </div>
        <div>
          <p className="text-gray-600"><strong>Account Number:</strong></p>
          <p className="font-medium">1234567890</p>
        </div>
        <div>
          <p className="text-gray-600"><strong>IBAN:</strong></p>
          <p className="font-medium">PK36SCBL0000001123456789</p>
        </div>
      </div>
    </div>

    {/* Total Withdrawals Card - Takes 1/3 width on larger screens */}
    <div className="flex-1 md:flex-[1] min-w-[250px]">
      <SummaryCard 
        title="Total Withdrawals" 
        amount={`Rs. ${data?.data?.withdrawn }`} 
        icon={<BsCashStack className="text-purple-600" />} 
        color="purple"
        className="h-full"
      />
    </div>
  </div>
</div>
      

      <TransactionTable data={transactions} />

      <div className="mt-6 text-right">
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
        >
          Request Payout
        </button>
      </div>

      <WithdrawalModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSubmit={handleRequestPayout}
      />
    </div>
  );
}